import { useEffect, useState } from "react";
import { useForm, type UseFormRegisterReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X } from "lucide-react";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { TreatmentPicker } from "@/components/treatment-picker";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";

const schema = z
  .object({
    code: z.string().min(1, "Kod jest wymagany").max(30),
    description: z.string().max(140, "Maksymalnie 140 znaków"),
    type: z.enum(["percentage", "fixed_amount"]),
    value: z.coerce.number().min(1, "Wartość musi być > 0"),
    minPurchase: z.coerce.number().min(1).optional().or(z.literal("")),
    maxUses: z.coerce.number().int().min(1).optional().or(z.literal("")),
    usesPerUser: z.coerce.number().int().min(1, "Co najmniej 1").max(100),
    // `yyyy-mm-dd`; the coupon runs from the start of the first day to the
    // end of the last one, in the browser's time zone.
    validFrom: z.string(),
    validUntil: z.string(),
    scope: z.enum(["all", "selected"]),
    treatmentIds: z.array(z.string()),
    tagIds: z.array(z.string()),
    audience: z.enum(["code", "all", "selected"]),
    notify: z.boolean(),
    isActive: z.boolean(),
  })
  .refine((d) => !d.validFrom || !d.validUntil || d.validUntil >= d.validFrom, {
    path: ["validUntil"],
    message: "Koniec nie może być wcześniejszy niż początek",
  })
  .refine(
    (d) => d.scope === "all" || d.treatmentIds.length + d.tagIds.length > 0,
    {
      path: ["scope"],
      message: "Wybierz co najmniej jeden zabieg lub kategorię",
    },
  )
  .refine((d) => d.type !== "percentage" || d.value <= 100, {
    path: ["value"],
    message: "Rabat procentowy to najwyżej 100%",
  });

type FormData = z.infer<typeof schema>;

type Audience = FormData["audience"];

type Coupon = {
  id: string;
  code: string;
  description: string | null;
  type: "percentage" | "fixed_amount";
  value: number;
  minPurchaseAmount: number | null;
  maxUses: number | null;
  usesPerUser: number;
  treatmentIds: string[];
  tagIds: string[];
  scoped: boolean;
  assignedCount: number;
  audience: Audience;
  validFrom: Date | string | null;
  validUntil: Date | string | null;
  isActive: boolean;
};

export type CouponRecipient = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
};

const AUDIENCE_HINT: Record<Audience, string> = {
  code: "Klient dodaje kupon, wpisując kod w aplikacji.",
  all: "Kupon trafia do portfela każdego klienta salonu — także tych, którzy dołączą później.",
  selected:
    "Kupon dostają tylko wskazane osoby. Kod nie zadziała u nikogo innego.",
};

const EMPTY: FormData = {
  code: "",
  description: "",
  type: "percentage",
  value: 10,
  minPurchase: "",
  maxUses: "",
  usesPerUser: 1,
  validFrom: "",
  validUntil: "",
  scope: "all",
  treatmentIds: [],
  tagIds: [],
  audience: "code",
  notify: true,
  isActive: true,
};

function recipientName(
  r: Pick<CouponRecipient, "firstName" | "lastName" | "email">,
) {
  return [r.firstName, r.lastName].filter(Boolean).join(" ") || r.email;
}

/** Date → value of a `date` input, in the browser's time zone. */
function toDateInput(value: Date | string | null): string {
  if (!value) return "";
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/** Saturday and Sunday of the current or the coming weekend. */
function weekendRange(now: Date) {
  const day = now.getDay(); // 0 = Sunday … 6 = Saturday
  const saturday = addDays(now, day === 0 ? -1 : 6 - day);
  return { from: saturday, until: addDays(saturday, 1) };
}

const VALIDITY_PRESETS: {
  label: string;
  range: (now: Date) => { from: Date | null; until: Date | null };
}[] = [
  { label: "Dziś", range: (now) => ({ from: null, until: now }) },
  { label: "Ten weekend", range: weekendRange },
  { label: "7 dni", range: (now) => ({ from: null, until: addDays(now, 7) }) },
  {
    label: "30 dni",
    range: (now) => ({ from: null, until: addDays(now, 30) }),
  },
  { label: "Bez terminu", range: () => ({ from: null, until: null }) },
];

export function CouponDialog({
  open,
  onOpenChange,
  coupon,
  presetRecipient,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  coupon?: Coupon | null;
  /** Open as "gift a coupon to this client" — a new coupon for one person. */
  presetRecipient?: CouponRecipient | null;
}) {
  const utils = trpc.useUtils();
  const isEdit = !!coupon;

  // Who holds the coupon right now. Shares its cache entry with the list in
  // RecipientsField, so taking a coupon back there is reflected here at
  // once — `coupon.assignedCount` is only as fresh as the table behind us.
  const { data: holders } = trpc.coupon.recipients.useQuery(
    { id: coupon?.id ?? "" },
    { enabled: open && isEdit },
  );
  const holderCount = holders?.length ?? coupon?.assignedCount ?? 0;

  // Clients picked in this session; on edit these are the ones to add.
  const [picked, setPicked] = useState<CouponRecipient[]>([]);

  function onSaved(message: string, notified: number) {
    utils.coupon.list.invalidate();
    utils.coupon.recipients.invalidate();
    toast.success(
      notified > 0 ? `${message} · wysłano powiadomień: ${notified}` : message,
    );
    onOpenChange(false);
  }

  const createMutation = trpc.coupon.create.useMutation({
    onSuccess: (res) => onSaved("Kupon dodany", res.notified),
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.coupon.update.useMutation({
    onSuccess: (res) => onSaved("Kupon zaktualizowany", res.notified),
    onError: (err) => toast.error(err.message),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    if (coupon) {
      form.reset({
        code: coupon.code,
        description: coupon.description ?? "",
        type: coupon.type,
        value: coupon.type === "percentage" ? coupon.value : coupon.value / 100,
        minPurchase: coupon.minPurchaseAmount
          ? coupon.minPurchaseAmount / 100
          : "",
        maxUses: coupon.maxUses ?? "",
        usesPerUser: coupon.usesPerUser,
        validFrom: toDateInput(coupon.validFrom),
        validUntil: toDateInput(coupon.validUntil),
        scope:
          // `scoped` also covers a coupon whose treatments were all deleted:
          // it opens as limited-to-nothing and has to be fixed, not as "all".
          coupon.scoped ? "selected" : "all",
        treatmentIds: coupon.treatmentIds,
        tagIds: coupon.tagIds,
        audience: coupon.audience,
        notify: true,
        isActive: coupon.isActive,
      });
      setPicked([]);
    } else {
      form.reset({
        ...EMPTY,
        audience: presetRecipient ? "selected" : "code",
      });
      setPicked(presetRecipient ? [presetRecipient] : []);
    }
  }, [open, coupon, presetRecipient, form]);

  const watchType = form.watch("type");
  const watchAudience = form.watch("audience");
  const errors = form.formState.errors;

  const onSubmit = form.handleSubmit((data) => {
    // A "selected clients" coupon has to be held by someone: on create that
    // means picking a client; on edit an existing holder is enough.
    if (
      data.audience === "selected" &&
      picked.length === 0 &&
      holderCount === 0
    ) {
      toast.error("Wybierz co najmniej jednego klienta");
      return;
    }

    const value =
      data.type === "percentage"
        ? Math.round(data.value)
        : Math.round(data.value * 100);

    const shared = {
      code: data.code,
      description: data.description.trim() || null,
      type: data.type,
      value,
      minPurchaseAmount:
        data.minPurchase !== "" && data.minPurchase !== undefined
          ? Math.round(Number(data.minPurchase) * 100)
          : null,
      maxUses:
        data.maxUses !== "" && data.maxUses !== undefined
          ? Number(data.maxUses)
          : null,
      usesPerUser: data.usesPerUser,
      // No time zone suffix, so these are the browser's local day bounds.
      validFrom: data.validFrom
        ? new Date(`${data.validFrom}T00:00:00`).toISOString()
        : null,
      validUntil: data.validUntil
        ? new Date(`${data.validUntil}T23:59:59.999`).toISOString()
        : null,
      treatmentIds: data.scope === "selected" ? data.treatmentIds : [],
      tagIds: data.scope === "selected" ? data.tagIds : [],
      audience: data.audience,
      userIds:
        data.audience === "selected" ? picked.map((p) => p.id) : undefined,
      notify: data.audience !== "code" && data.notify,
    };

    if (isEdit && coupon) {
      updateMutation.mutate({
        id: coupon.id,
        ...shared,
        isActive: data.isActive,
      });
    } else {
      createMutation.mutate(shared);
    }
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  // On edit a push only makes sense for the clients being added now — the
  // ones who already hold the coupon were told when they got it.
  const canNotify =
    watchAudience === "selected"
      ? picked.length > 0
      : watchAudience === "all" && !isEdit;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? "Edytuj kupon"
              : presetRecipient
                ? `Kupon dla: ${recipientName(presetRecipient)}`
                : "Nowy kupon"}
          </DialogTitle>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="c-code">Kod kuponu</Label>
              <Input
                id="c-code"
                className="font-mono uppercase"
                {...form.register("code")}
              />
              {errors.code && (
                <p className="text-sm text-destructive">{errors.code.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="c-desc">Opis dla klienta (opcjonalnie)</Label>
              <Input
                id="c-desc"
                maxLength={140}
                placeholder="np. Dziękujemy za polecenie!"
                {...form.register("description")}
              />
              <p className="text-xs text-muted-foreground">
                Widoczny na kuponie w aplikacji i w powiadomieniu push.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="c-type">Typ rabatu</Label>
                <select
                  id="c-type"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                  {...form.register("type")}
                >
                  <option value="percentage">Procentowy (%)</option>
                  <option value="fixed_amount">Kwotowy (zł)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-value">
                  Wartość {watchType === "percentage" ? "(%)" : "(zł)"}
                </Label>
                <Input
                  id="c-value"
                  type="number"
                  step={watchType === "percentage" ? "1" : "0.01"}
                  min="1"
                  {...form.register("value")}
                />
                {errors.value && (
                  <p className="text-sm text-destructive">
                    {errors.value.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="c-min">Min. wartość zamówienia (zł)</Label>
              <Input
                id="c-min"
                type="number"
                step="0.01"
                min="1"
                placeholder="Bez minimum"
                {...form.register("minPurchase")}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="c-max">Pula kuponów</Label>
                <Input
                  id="c-max"
                  type="number"
                  min="1"
                  placeholder="Bez limitu"
                  {...form.register("maxUses")}
                />
                <p className="text-xs text-muted-foreground">
                  Ile razy kupon może zostać wykorzystany łącznie, przez
                  wszystkich klientów.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-per-user">Użyć na osobę</Label>
                <Input
                  id="c-per-user"
                  type="number"
                  min="1"
                  max="100"
                  {...form.register("usesPerUser")}
                />
                <p className="text-xs text-muted-foreground">
                  Ile razy jeden klient może użyć tego kuponu.
                </p>
                {errors.usesPerUser && (
                  <p className="text-sm text-destructive">
                    {errors.usesPerUser.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="c-scope">Na co działa</Label>
              <select
                id="c-scope"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                {...form.register("scope")}
              >
                <option value="all">Wszystkie zabiegi</option>
                <option value="selected">Wybrane zabiegi lub kategorie</option>
              </select>
              {form.watch("scope") === "selected" && (
                <ScopeField
                  enabled={open}
                  treatmentIds={form.watch("treatmentIds")}
                  tagIds={form.watch("tagIds")}
                  onTreatmentIdsChange={(ids) =>
                    form.setValue("treatmentIds", ids, { shouldValidate: true })
                  }
                  onTagIdsChange={(ids) =>
                    form.setValue("tagIds", ids, { shouldValidate: true })
                  }
                />
              )}
              {errors.scope && (
                <p className="text-sm text-destructive">{errors.scope.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Ważność</Label>
              <div className="flex flex-wrap gap-2">
                {VALIDITY_PRESETS.map((preset) => (
                  <Button
                    key={preset.label}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const { from, until } = preset.range(new Date());
                      form.setValue("validFrom", toDateInput(from));
                      form.setValue("validUntil", toDateInput(until), {
                        shouldValidate: true,
                      });
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label
                    htmlFor="c-from"
                    className="text-xs text-muted-foreground"
                  >
                    Od dnia (puste = od razu)
                  </Label>
                  <DateField
                    id="c-from"
                    emptyLabel="Od razu"
                    value={form.watch("validFrom")}
                    inputProps={form.register("validFrom")}
                  />
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor="c-until"
                    className="text-xs text-muted-foreground"
                  >
                    Do dnia włącznie (puste = bezterminowo)
                  </Label>
                  <DateField
                    id="c-until"
                    emptyLabel="Bezterminowo"
                    value={form.watch("validUntil")}
                    inputProps={form.register("validUntil")}
                  />
                </div>
              </div>
              {errors.validUntil && (
                <p className="text-sm text-destructive">
                  {errors.validUntil.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="c-audience">Kto dostaje kupon</Label>
              <select
                id="c-audience"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                {...form.register("audience")}
              >
                <option value="code">Osoby, które wpiszą kod</option>
                <option value="all">Wszyscy klienci — automatycznie</option>
                <option value="selected">Wybrani klienci</option>
              </select>
              <p className="text-xs text-muted-foreground">
                {AUDIENCE_HINT[watchAudience]}
              </p>
            </div>

            {watchAudience === "selected" && (
              <RecipientsField
                couponId={coupon?.id ?? null}
                picked={picked}
                onPickedChange={setPicked}
              />
            )}

            {canNotify && (
              <div className="flex items-center gap-3">
                <Switch
                  id="c-notify"
                  checked={form.watch("notify")}
                  onCheckedChange={(v) => form.setValue("notify", v)}
                />
                <Label htmlFor="c-notify">
                  Wyślij powiadomienie push o nowym kuponie
                </Label>
              </div>
            )}

            {isEdit && (
              <div className="flex items-center gap-3">
                <Switch
                  id="c-active"
                  checked={form.watch("isActive")}
                  onCheckedChange={(v) => form.setValue("isActive", v)}
                />
                <Label htmlFor="c-active">
                  {form.watch("isActive") ? "Aktywny" : "Nieaktywny"}
                </Label>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Anuluj
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? "Zapisywanie..."
                : isEdit
                  ? "Zapisz zmiany"
                  : "Dodaj kupon"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Date input that says what "empty" means. Left to the browser, an empty
 * date field shows a format hint — and Safari shows today's date in it,
 * which reads as a chosen date. So while empty and not being edited, the
 * native text is hidden behind our own label.
 */
function DateField({
  id,
  emptyLabel,
  value,
  inputProps,
}: {
  id: string;
  emptyLabel: string;
  value: string;
  inputProps: UseFormRegisterReturn;
}) {
  const isEmpty = !value;
  return (
    <div className="relative">
      <Input
        id={id}
        type="date"
        className={
          isEmpty ? "peer text-transparent focus:text-foreground" : undefined
        }
        {...inputProps}
      />
      {isEmpty && (
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground peer-focus:hidden">
          {emptyLabel}
        </span>
      )}
    </div>
  );
}

function toggled(ids: string[], id: string) {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

/**
 * Limits the coupon to categories (every treatment in them, including ones
 * added later) and/or treatments picked one by one.
 */
function ScopeField({
  enabled,
  treatmentIds,
  tagIds,
  onTreatmentIdsChange,
  onTagIdsChange,
}: {
  enabled: boolean;
  treatmentIds: string[];
  tagIds: string[];
  onTreatmentIdsChange: (ids: string[]) => void;
  onTagIdsChange: (ids: string[]) => void;
}) {
  const { data: tags } = trpc.tag.list.useQuery(undefined, { enabled });
  // Treatments under a running offer that cannot be combined with coupons:
  // a coupon scoped to one would work nowhere, so they are not offered here.
  const { data: blocked } = trpc.coupon.blockedTreatments.useQuery(undefined, {
    enabled,
  });
  // An empty category would limit the coupon to nothing. One already on the
  // coupon stays listed so it can be unticked.
  const pickableTags = (tags ?? []).filter(
    (tag) => tag.treatmentIds.length > 0 || tagIds.includes(tag.id),
  );
  const blockedIds = new Set((blocked ?? []).map((b) => b.treatmentId));
  const blockedInPickedTags = (tags ?? [])
    .filter((tag) => tagIds.includes(tag.id))
    .flatMap((tag) => tag.treatmentIds)
    .filter((id) => blockedIds.has(id));
  const blockedPicked = (blocked ?? []).filter((b) =>
    treatmentIds.includes(b.treatmentId),
  );

  return (
    <div className="space-y-3 rounded-md border p-3">
      {pickableTags.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">
            Kategorie — kupon obejmie wszystkie zabiegi z kategorii
          </p>
          <div className="flex flex-wrap gap-2">
            {pickableTags.map((tag) => (
              <label
                key={tag.id}
                className="flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1 text-sm hover:bg-muted/50"
              >
                <input
                  type="checkbox"
                  checked={tagIds.includes(tag.id)}
                  onChange={() => onTagIdsChange(toggled(tagIds, tag.id))}
                />
                {tag.name}
                <span className="text-xs text-muted-foreground">
                  ({tag.treatmentIds.length})
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">
          Pojedyncze zabiegi ({treatmentIds.length})
        </p>
        {/* Enter in the picker's search box would submit the coupon form. */}
        <div
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault();
          }}
        >
          <TreatmentPicker
            mode="multi"
            selectedIds={treatmentIds}
            onPick={(id) => onTreatmentIdsChange(toggled(treatmentIds, id))}
            // Keep ones already on the coupon visible so they can be removed.
            excludeIds={[...blockedIds].filter(
              (id) => !treatmentIds.includes(id),
            )}
            enabled={enabled}
            listClassName="max-h-48"
            emptyText="Brak zabiegów. Najpierw dodaj zabiegi."
          />
        </div>
        {blocked && blocked.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Ukryto zabiegi z ofertą, której nie można łączyć z kuponami.
          </p>
        )}
        {blockedPicked.length > 0 && (
          <p className="text-xs text-destructive">
            Na tych zabiegach kupon teraz nie zadziała (trwa oferta bez łączenia
            z kuponami): {blockedPicked.map((b) => b.treatmentName).join(", ")}.
          </p>
        )}
        {blockedInPickedTags.length > 0 && (
          <p className="text-xs text-muted-foreground">
            W wybranych kategoriach{" "}
            {blockedInPickedTags.length === 1 ? "jest zabieg" : "są zabiegi"} z
            taką ofertą — tam kupon nie zadziała, dopóki oferta trwa.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Who holds the coupon (when editing) plus a search box to add more clients.
 * Clients picked here are only assigned when the dialog is saved; taking a
 * coupon back from someone is immediate.
 */
function RecipientsField({
  couponId,
  picked,
  onPickedChange,
}: {
  couponId: string | null;
  picked: CouponRecipient[];
  onPickedChange: (next: CouponRecipient[]) => void;
}) {
  const utils = trpc.useUtils();
  const [search, setSearch] = useState("");
  const term = search.trim();

  const { data: current } = trpc.coupon.recipients.useQuery(
    { id: couponId ?? "" },
    { enabled: !!couponId },
  );

  const { data: found, isFetching } = trpc.clients.list.useQuery(
    { page: 1, limit: 8, search: term },
    { enabled: term.length >= 2 },
  );

  const unassign = trpc.coupon.unassign.useMutation({
    onSuccess: () => {
      utils.coupon.recipients.invalidate();
      utils.coupon.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const taken = new Set([
    ...picked.map((p) => p.id),
    ...(current ?? []).map((r) => r.userId),
  ]);
  const candidates = (found?.items ?? []).filter((c) => !taken.has(c.id));

  return (
    <div className="space-y-3 rounded-md border p-3">
      {current && current.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">
            Mają ten kupon ({current.length})
          </p>
          {current.length >= 500 && (
            <p className="text-xs text-muted-foreground">
              Pokazano pierwszych 500 osób.
            </p>
          )}
          <ul className="max-h-40 space-y-1 overflow-y-auto">
            {current.map((r) => (
              <li
                key={r.userId}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span className="truncate">
                  {recipientName(r)}
                  <span className="ml-2 text-xs text-muted-foreground">
                    {r.email}
                  </span>
                </span>
                {r.usedAt ? (
                  <Badge variant="secondary">Wykorzystany</Badge>
                ) : r.usedCount > 0 ? (
                  <Badge variant="secondary">Użyty {r.usedCount}×</Badge>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={unassign.isPending}
                    onClick={() => {
                      if (couponId && confirm("Zabrać kupon temu klientowi?")) {
                        unassign.mutate({ id: couponId, userId: r.userId });
                      }
                    }}
                  >
                    Zabierz
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {picked.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {picked.map((p) => (
            <Badge key={p.id} variant="outline" className="gap-1 pr-1">
              {recipientName(p)}
              <button
                type="button"
                aria-label={`Usuń: ${recipientName(p)}`}
                className="rounded-full p-0.5 hover:bg-muted"
                onClick={() =>
                  onPickedChange(picked.filter((x) => x.id !== p.id))
                }
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <Input
          placeholder="Szukaj klienta po imieniu, nazwisku lub e-mailu"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          // Enter here would submit the whole coupon form.
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault();
          }}
        />
        {term.length >= 2 && (
          <ul className="space-y-1">
            {candidates.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    onPickedChange([
                      ...picked,
                      {
                        id: c.id,
                        firstName: c.firstName,
                        lastName: c.lastName,
                        email: c.email,
                      },
                    ]);
                    setSearch("");
                  }}
                >
                  <span>{recipientName(c)}</span>
                  <span className="text-xs text-muted-foreground">
                    {c.email}
                  </span>
                </button>
              </li>
            ))}
            {!isFetching && candidates.length === 0 && (
              <li className="px-2 py-1.5 text-sm text-muted-foreground">
                Brak pasujących klientów
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
